// A table's selection bar ("N selected · …" and its actions), its space kept at all times: with
// nothing ticked it says what ticking does, in the same height, so the rows under it never move when
// the first one is ticked or the last one unticked — the next click lands on the row it aims at.

import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { watchHeight } from "./sticky";

/**
 * An element's height, measured as it changes (a bar that wraps to two lines, its hint swapped for its
 * actions): 0 while it isn't there. A ref callback, so it starts measuring whenever the element mounts —
 * also after a loading state rendered without it.
 */
export function useHeight<T extends HTMLElement>(): [(el: T | null) => void, number] {
  const [height, setHeight] = useState(0);
  const stop = useRef<(() => void) | null>(null);
  const ref = useCallback((el: T | null) => {
    stop.current?.();
    stop.current = el ? watchHeight(el, setHeight) : null;
    if (!el) setHeight(0);
  }, []);
  return [ref, height];
}

/** The bar's height whatever it holds: one row of `h-8` actions, `py-1.5` and the border fit in 48 px. */
const RESERVED = "min-h-12";

export function SelectionBar({
  active,
  hint,
  tone = "muted",
  className,
  children,
  ...rest
}: {
  /** Something is ticked: the bar's actions show. */
  active: boolean;
  /** What ticking does, quiet, while nothing is. */
  hint: ReactNode;
  /** `dark`: the Worklist's and Shipping's black bar; `muted`: the Admin lists'. */
  tone?: "dark" | "muted";
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-md border px-3 py-1.5",
        RESERVED,
        active
          ? tone === "dark"
            ? "bg-foreground text-background border-transparent text-sm"
            : "bg-muted/60 text-[13px]"
          : "text-muted-foreground border-dashed text-xs",
        className,
      )}
      data-selection-bar={active ? "active" : "idle"}
      {...rest}
    >
      {active ? children : hint}
    </div>
  );
}
